import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  OnDestroy,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChildren,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { BaseComponent } from '../BaseComponent';
import { AbstractControl, FormGroup } from '@angular/forms';
import { FontTypeEnum } from 'src/app/model/enums/FontTypeEnum';
import { TranslateService } from '@ngx-translate/core';
import { SessionService } from '../../session-service.service';
import { ComponentCommunicationService } from '../../component-communication.service';
import { BaseOption } from '../../model/BaseOption';
import { isDate, formatISO } from 'date-fns';

enum KEY_CODES {
  DOWN_ARROW = 'ArrowDown',
  UP_ARROW = 'ArrowUp',
}

@Component({
  selector: 'app-select-box',
  templateUrl: './select-box.component.html',
  styleUrls: ['./select-box.component.css']
})
export class SelectBoxComponent extends BaseComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input()
  optionsList: BaseOption[];

  @Input()
  value: any;

  @Input()
  defaultOptionCaptionKey = 'Common.DefaultOptionCaption';

  @Input()
  showCallToActionButton = false;

  @Input()
  callToActionCaptionKey = 'Calculation.PremiumSelectButton';

  @Input()
  isDisabled = false;

  @Input()
  fontFamily: any;

  @Input()
  fontColor: any;

  @Input()
  fontSize: string;

  @Input()
  textColor: any;

  @Input()
  inputColor: string;

  @Input()
  inputBorderColor: string;

  @Input()
  backgroundHoverColor: string;

  @Input()
  backgroundHoverTextColor: string;

  @Input()
  formGroup: FormGroup;

  @Input()
  additionalInfoInsurer: string = null;

  @Input()
  itemTemplate?: TemplateRef<any>;

  @Input()
  forceOptionListOverflow?: boolean;

  @Output()
  selectedOption = new EventEmitter<BaseOption>();

  controlType = this.constructor.name;
  FontTypeEnum = FontTypeEnum;
  isOptionListVisible = false;
  currentlySelectedOption: BaseOption | null = null;
  currentlyFocusedOption = 0;
  registeredOptionListItemEventListeners = [];
  forcedOverflowWidth = 0;

  @ViewChildren('optionListItemElement') optionListItems: QueryList<ElementRef>;
  @ViewChild('optionList') optionList: ElementRef;

  valueChangesSubscription: Subscription;

  constructor(translateService: TranslateService,
              sessionService: SessionService,
              private componentCommunicationService: ComponentCommunicationService,
              private renderer: Renderer2,
              private elementRef: ElementRef) {
    super(translateService, sessionService);
  }

  updateValue(newValue): void {
    this.currentlySelectedOption = this.optionsList.find(option => {
      if (isDate(newValue)) {
        return formatISO(option.value, { representation: 'date' }) ===
          formatISO(newValue, { representation: 'date' });
      } else {
        return option.value === newValue;
      }
    });
  }

  ngOnInit(): void {
    if (this.value !== undefined) {
      this.control.setValue(this.value);
    }
    if (this.control.value) {
      this.updateValue(this.control.value);
    }
    const validator = this.control.validator
      ? this.control.validator({} as AbstractControl)
      : {};
    this.isRequired = validator?.required;

    this.valueChangesSubscription = this.control.valueChanges.pipe()
      .subscribe(newValue => {
        this.updateValue(newValue);
    });
    super.ngOnInit();
  }

  ngAfterViewInit(): void {
    this.setUpKeyEventListeners(this.optionListItems);

    this.optionListItems.changes.subscribe((items: QueryList<ElementRef>) => {
      this.setUpKeyEventListeners(items);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.optionsList && !changes.optionsList.firstChange) {
      this.setUpKeyEventListeners(this.optionListItems);
    }
  }

  ngOnDestroy(): void {
    this.destroyKeyEventListeners();
    this.valueChangesSubscription.unsubscribe();
  }

  private setUpKeyEventListeners(optionListItems: QueryList<ElementRef>): void {
    this.destroyKeyEventListeners();

    optionListItems.forEach(item => {
      this.registeredOptionListItemEventListeners.push(
        this.renderer.listen(item.nativeElement, 'keydown', (event) => this.optionListItemKeyDownHandler(event))
      );
    });
  }

  private destroyKeyEventListeners(): void {
    this.registeredOptionListItemEventListeners.forEach(listener => listener());
  }

  private cycleOptionListForward(): void {
    if (this.currentlyFocusedOption === this.optionListItems.length - 1) {
      this.focusOptionListItem(0);
    } else {
      this.focusOptionListItem(this.currentlyFocusedOption + 1);
    }
  }

  private cycleOptionListBackward(): void {
    if (this.currentlyFocusedOption === 0) {
      this.focusOptionListItem(this.optionListItems.length - 1);
    } else {
      this.focusOptionListItem(this.currentlyFocusedOption - 1);
    }
  }

  private focusOptionListItem(index: number): void {
    setTimeout(() => {
      this.optionListItems.toArray()[index].nativeElement.focus();
    });

    this.currentlyFocusedOption = index;
  }

  private optionListItemKeyDownHandler(event: KeyboardEvent): void {
    if (event.key === KEY_CODES.DOWN_ARROW) {
      this.cycleOptionListForward();
    } else if (event.key === KEY_CODES.UP_ARROW) {
      this.cycleOptionListBackward();
    }
  }

  toggleListVisibility(): void {
    this.isOptionListVisible = !this.isOptionListVisible;

    if (this.isOptionListVisible) {
      this.focusOptionListItem(0);

      if (this.forceOptionListOverflow) {
        const elementWidth = this.elementRef.nativeElement.getBoundingClientRect().width;
        if (this.forcedOverflowWidth !== elementWidth) {
          this.renderer.setStyle(this.optionList.nativeElement, 'width', `${elementWidth}px`);
          this.forcedOverflowWidth = elementWidth;
        }
      }
    }
  }

  closeToggleList(): void {
    this.isOptionListVisible = false;
  }

  openPopup(): void {
    this.componentCommunicationService.loadDialog(this.additionalInfoInsurer);
  }

  selectValue(option: BaseOption): void {
    this.control.setValue(option.value);
    this.selectedOption.emit(option);
    this.currentlySelectedOption = option;
    this.toggleListVisibility();
  }
}
